import React, { useState, useEffect } from "react";
import { X, Edit, Plus, AlertCircle, ArrowLeft, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import Swal from 'sweetalert2';

import { gamesApi } from "../../api/games";
import { useAuth } from "../../context/AuthContext";
import Profile from "../UpdateUser/Profile/Profile";
import Log from "../UpdateUser/Log/Log";
import UserHeirarchy from "../UserHeirarchy/UserHeirarchy";
import UserManagement from "../UpdateUser/UserManagement/UserManagement";
import { userApi } from "../../api/user";

export default function UserGames() {
  const { tokens, user } = useAuth();
  const navigate = useNavigate();
  const { userId } = useParams();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentGame, setCurrentGame] = useState(null);
  const [games, setGames] = useState([]);
  const [gameSettings, setGameSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [userData, setUserData] = useState(null);
  const [userGames, setUserGames] = useState([]);
  const [userGamesDropdownOpen, setUserGamesDropdownOpen] = useState(false);
  const [gameSettingsDropdownOpen, setGameSettingsDropdownOpen] = useState(false);
  const [selectedGame, setSelectedGame] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    gameSettingsId: "",
    gameId: "",
    commissionRate: "",
    winRate: "",
    doubleWin: "",
    tripleWin: "",
    bet_limit: ""
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch user data
        const userResponse = await userApi.getUserById(tokens.access.token, userId);
        if (userResponse.status === 200) {
          setUserData({ user: userResponse.data.user });
        }
        console.log('User Data', userResponse.data.user.parent.id)
        console.log('Current User Data', user.id)
        
        
        // Fetch games
        const gamesResponse = await gamesApi.getUserGame(tokens.access.token, userId);
        setGames(gamesResponse.data);
        
        // Fetch game settings
        const settingsResponse = await gamesApi.getAllGames(tokens.access.token);
        if (settingsResponse.status === 200) {
          setGameSettings(settingsResponse.data);
        }
        // Fetch user games for non-admin users
        // if (user && user.roleId !== 1) {
          const userGamesResponse = await gamesApi.getUserGames(tokens.access.token);
          console.log('User Games', userGamesResponse)
          if (userGamesResponse) {
            setUserGames(userGamesResponse.map(game => ({
              ...game,
              dropdownGameName: game.name + ` (₱${toCurrency(game.winRate)}|${toCurrency(game.commissionRate)}%)`
            })));
          }
        // }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load user data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [userId, tokens.access.token, user]);


  // Helper function to format currency
  const toCurrency = (value) => {
    return parseFloat(value).toFixed(2);
  };

  const handleEditClick = (game) => {
    const parentGame = userGames.find(g => g.id === game.gameId);
    console.log('Parent Game', parentGame)
    setCurrentGame(game);

    if(parentGame){
      setSelectedGame(`${parentGame.name} (₱${toCurrency(parentGame.winRate)}|${toCurrency(parentGame.commissionRate)}%)`); // Clear selected game
    }
    setFormData({
      id: game.id,
      name: game.name || "",
      description: game.description || "",
      gameSettingsId: game.game_settings_id?.toString() || "",
      gameId: game.gameId?.toString() || "",
      commissionRate: game.commissionRate?.toString() || "",
      winRate: game.winRate?.toString() || "",
      doubleWin: game.doubleWin?.toString() || "",
      tripleWin: game.tripleWin?.toString() || "",
      bet_limit: game.bet_limit?.toString() || ""
    });
    setShowEditModal(true);
  };

  const handleAddClick = () => {
    setSelectedGame(""); // Clear selected game
    setFormData({
      name: "",
      description: "",
      gameSettingsId: "",
      commissionRate: 0,
      winRate: 0,
      doubleWin: 0,
      tripleWin: 0,
      bet_limit: 0
    });
    setShowAddModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Prepare payload with numeric values where needed
      const payload = {
        name: formData.name,
        description: `User: ${userData.user.name} - ${formData.name} (${formData.commissionRate}%)`,
        rules: { maxBet: parseInt(formData.bet_limit) },
        commissionRate: parseFloat(formData.commissionRate),
        winRate: parseInt(formData.winRate),
        doubleWin: parseInt(formData.doubleWin),
        tripleWin: parseInt(formData.tripleWin),
        gameSettingsId: parseInt(formData.gameSettingsId),
        userId: parseInt(userId),
        gameId: parseInt(formData.gameId),
        bet_limit: parseInt(formData.bet_limit)
      };

      const response = await gamesApi.createUserSpecificGame(
        tokens.access.token,
        userId,
        payload
      );

      if (response.status === 201 || response.status === 200) {
        toast.success("User-specific game created successfully");
        setShowAddModal(false);
        // Refresh games list
        const updatedGames = await gamesApi.getUserGame(tokens.access.token, userId);
        setGames(updatedGames.data);
        // Reset form
        setFormData({
          name: "",
          description: "",
          gameSettingsId: "",
          commissionRate: "",
          winRate: "",
          doubleWin: "",
          tripleWin: "",
        });
      } else {
        toast.error("Failed to create user-specific game");
      }
    } catch (error) {
      console.error("Error creating user-specific game:", error);
      toast.error("Error creating user-specific game");
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Prepare payload with numeric values where needed
      const payload = {
        name: formData.name,
        description: `User: ${userData.user.name} - ${formData.name} (${formData.commissionRate}%)`,
        rules: { maxBet: parseInt(formData.bet_limit) },
        commissionRate: parseFloat(formData.commissionRate),
        winRate: parseInt(formData.winRate),
        doubleWin: parseInt(formData.doubleWin),
        tripleWin: parseInt(formData.tripleWin),
        gameSettingsId: parseInt(formData.gameSettingsId),
        userId: parseInt(userId),
        gameId: parseInt(formData.gameId),
        active: currentGame?.active !== false
      };

      if (user && user.roleId === 1) {
        delete payload.gameId;
      }

      // Use the updateGame API to update the game
      const response = await gamesApi.updateGame(
        tokens.access.token, 
        currentGame.id,
        payload
      );

      if (response.status === 200) {
        toast.success("Game updated successfully");
        setShowEditModal(false);
        // Refresh games list
        const updatedGames = await gamesApi.getUserGame(tokens.access.token, userId);
        setGames(updatedGames.data);
      } else {
        toast.error("Failed to update game");
      }
    } catch (error) {
      console.error("Error updating game:", error);
      toast.error("Error updating game");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async (gameId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "This action cannot be undone.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      const response = await gamesApi.deleteGame(tokens.access.token, gameId);
      if (response.status === 204) {
        toast.success("Game deleted successfully");
        setShowEditModal(false);
        const updatedGames = await gamesApi.getUserGame(tokens.access.token, userId);
        setGames(updatedGames.data);
      } else {
        toast.error("Failed to delete game");
      }
    }
  };

  // Tab components
  const renderGamesTab = () => (
    <>
      {/* Game selector and Add button */}
      <div className="flex items-center px-2 pt-5">
        <div className="flex-1">
          <label className="block mb-2 text-xs font-medium text-gray-900 dark:text-white">Select Game</label>
          <select id="small" className="block w-full p-1 mb-6 text-[12px] text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500">
          <option>All</option>
          </select>
        </div>
        
        {user.id === userData.user.parent.id && (
          <button 
            onClick={handleAddClick}
            className="bg-blue-600 text-white rounded flex items-center px-3 py-2 ml-3 text-[12px]"
        >
            <Plus size={16} className="mr-1" /> Add Game
          </button>
        )}
      </div>

      {/* Pagination info */}
      <div className="flex justify-between items-center px-4 py-2 text-xs">
        <div className="flex items-center">
          <span className="text-gray-700">Items per page</span>
          <select className="border rounded ml-2 px-1.5 py-0 text-[12px]">
            <option>15</option>
          </select>
        </div>
        <div className="text-gray-700">1-10 of 100</div>
        <div className="flex items-center">
          <button className="px-2 py-1 text-gray-500">‹ Prev</button>
          <button className="px-2 py-1 text-blue-600 font-medium">Next ›</button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg">
        <table className="w-full text-sm text-left rtl:text-right text-gray-500 border dark:text-gray-400 border-gray-200 rounded-lg"
        >
          <thead className="text-xs text-gray-700 bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr className="text-left text-xs border-b bg-[#F7F9FC]" style={{color: "#64748B"}}>
              <th className="px-4 py-5 font-semibold whitespace-nowrap">Game</th>
              <th className="px-4 py-5 font-semibold whitespace-nowrap">Win</th>
              <th className="px-4 py-5 font-semibold whitespace-nowrap">Commission</th>
              <th className="px-4 py-5 font-semibold whitespace-nowrap">Double Win</th>
              <th className="px-4 py-5 font-semibold whitespace-nowrap">Triple Win</th>
              <th className="px-4 py-5 font-semibold whitespace-nowrap"> Action</th>
            </tr>
          </thead>
          <tbody>
            {games.map((game) => 
              (
                <tr key={game.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm whitespace-nowrap">{game.name}</td>
                  <td className="px-4 py-3 text-sm">{game.winRate}</td>
                  <td className="px-4 py-3 text-sm">{game.commissionRate}</td>
                  <td className="px-4 py-3 text-sm">{game.doubleWin}</td>
                  <td className="px-4 py-3 text-sm">{game.tripleWin}</td>
                  <td className="px-4 py-3 text-sm text-center">
                    <button className="text-blue-600" onClick={() => handleEditClick(game)}>
                      <Edit size={16} />
                    </button>
                  </td>  
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 bg-white z-10 pt-5 pb-3 px-4 border-b border-gray-200" style={{ zIndex: 1000}}>
        <div className="relative flex items-center">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 mr-2"
          >
            <ArrowLeft size={20} />
          </button>
          <h2 className="text-lg font-semibold">{userData?.user.name || 'User Games'}</h2>
        </div>

        {/* Tabs */}
        <div className="overflow-x-auto whitespace-nowrap flex bg-[#E1E7EF] px-1 py-1 rounded-lg mt-3">
          <button 
            className={`font-poppins flex-1 py-2 px-4 text-sm font-medium ${activeTab === 0 ? 'bg-white text-blue-800 rounded-lg shadow-md' : 'text-[#65758B]'}`}
            onClick={() => setActiveTab(0)}
          >
            Games
          </button>
          <button 
            className={`font-poppins flex-1 py-2 px-4 text-sm font-medium ${activeTab === 1 ? 'bg-white text-blue-800 rounded-lg shadow-md' : 'text-[#65758B]'}`}
            onClick={() => setActiveTab(1)}
          >
            Profile
          </button>
          <button 
            className={`font-poppins flex-1 py-2 px-4 text-sm font-medium ${activeTab === 2 ? 'bg-white text-blue-800 rounded-lg shadow-md' : 'text-[#65758B]'}`}
            onClick={() => setActiveTab(2)}
          >
            Log
          </button>
          {user && [1].includes(user.roleId) && (
            <button 
              className={`font-poppins flex-1 py-2 px-4 text-sm font-medium ${activeTab === 3 ? 'bg-white text-blue-800 rounded-lg shadow-md' : 'text-[#65758B]'}`}
              onClick={() => setActiveTab(3)}
            >
              User Management
            </button>
          )}
          {user && [1,2,3,4,5,6,7,8].includes(user.roleId) && (
            <button 
              className={`font-poppins flex-1 py-2 px-4 text-sm font-medium ${activeTab === 4 ? 'bg-white text-blue-800 rounded-lg shadow-md' : 'text-[#65758B]'}`}
              onClick={() => setActiveTab(4)}
          >
              Heirarchy
            </button>
          )}
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-4 py-4">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            {activeTab === 0 && renderGamesTab()}
            {activeTab === 1 && <Profile userData={userData} />}
            {activeTab === 2 && <Log userId={userId} />}
            {activeTab === 3 && <UserManagement userId={userId} userData={userData} />}
            {activeTab === 4 && <UserHeirarchy userId={userId}  />}
          </>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex justify-center items-center">
          <div className="bg-white rounded-lg w-full max-w-sm p-4 max-h-[90vh] overflow-auto animate-slide-up">
            <div className="mb-4">
              <div className="py-5 bg-[#0E64D1] rounded-t-lg text-white -mx-4 -mt-4 mb-4">
                <h3 className="text-lg font-semibold text-center">Edit Game</h3>
              </div>
              <button onClick={() => setShowEditModal(false)} className="absolute right-6 top-6 text-white">
                <X size={20} />
              </button>
            </div>
            
            {/* User info display */}
            <div className="mb-4 p-3 bg-blue-50 rounded-md border border-blue-100">
              <div className="flex items-start">
                <AlertCircle size={18} className="text-blue-500 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Editing game for user:</p>
                  <p className="text-sm text-blue-600">{userData?.user.name} (ID: {userId})</p>
                </div>
              </div>
            </div>
            
            <form onSubmit={handleEditSubmit}>
              {/* {user && user.roleId === 1 ? (
                <div className="mb-3">
                  <label className="block text-sm mb-1">Game Settings</label>
                  <select 
                    className="border rounded-md px-3 py-2 w-full"
                    name="gameSettingsId"
                    value={formData.gameSettingsId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Game Settings</option>
                    {gameSettings.map(setting => (
                      <option key={setting.id} value={setting.id}>{setting.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">The base game configuration</p>
                </div>
              ) : ( */}
                <div className="mb-3 relative">
                  <label className="block text-sm mb-1">Select Game</label>
                  <button
                    type="button"
                    onClick={() => setUserGamesDropdownOpen(!userGamesDropdownOpen)}
                    className="w-full text-sm px-3 py-2 bg-gray-50 border border-gray-300 rounded-md focus:outline-none flex justify-between items-center"
                  >
                    <span className="font-light text-sm text-gray-500">
                      {selectedGame || (currentGame ? `${currentGame.name} (₱${toCurrency(currentGame.winRate)}|${toCurrency(currentGame.commissionRate)}%)` : "Please Select Game")}
                    </span>
                    {userGamesDropdownOpen ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>

                  {userGamesDropdownOpen && (
                    <ul className="absolute w-full mt-1 bg-white border rounded-md shadow-md z-10 max-h-60 overflow-y-auto">
                      {userGames.map((game, index) => (
                        <li
                          key={game.id}
                          onClick={() => {
                            setSelectedGame(`${game.name} (₱${toCurrency(game.winRate)}|${toCurrency(game.commissionRate)}%)`);
                            setFormData({
                              ...formData,
                              gameSettingsId: game.game_settings_id?.toString() || "",
                              gameId: game.id?.toString() || "",
                              name: game.name || "",
                              commissionRate: game.commissionRate?.toString() || "",
                              winRate: game.winRate?.toString() || "",
                              doubleWin: game.doubleWin?.toString() || "",
                              tripleWin: game.tripleWin?.toString() || ""
                            });
                            setUserGamesDropdownOpen(false);
                          }}
                          className={`p-2.5 text-xs text-black hover:bg-[#E1E7EF] cursor-pointer ${index !== 0 ? "border-t border-[#E1E7EF]" : ""}`}
                        >
                          {game.dropdownGameName}
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Select a game to use as template</p>
                </div>
              {/* )} */}
              
              <div className="mb-3">
                <label className="block text-sm mb-1">Game Name</label>
                <input 
                  type="text"
                  className="border rounded-md px-3 py-2 w-full"
                  placeholder="Enter game name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="mb-3">
                <label className="block text-sm mb-1">Commission Rate (%)</label>
                <input 
                  type="number"
                  className="border rounded-md px-3 py-2 w-full"
                  placeholder="5.5"
                  name="commissionRate"
                  value={formData.commissionRate}
                  onChange={handleInputChange}
                  required
                  step="0.1"
                />
                <p className="text-xs text-gray-500 mt-1">Commission percentage for this game</p>
              </div>
              
              <div className="mb-3">
                <label className="block text-sm mb-1">Win Rate</label>
                <input 
                  type="number"
                  className="border rounded-md px-3 py-2 w-full"
                  placeholder="80"
                  name="winRate"
                  value={formData.winRate}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="mb-3">
                <label className="block text-sm mb-1">Double Win</label>
                <input 
                  type="number"
                  className="border rounded-md px-3 py-2 w-full"
                  placeholder="160"
                  name="doubleWin"
                  value={formData.doubleWin}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="mb-3">
                <label className="block text-sm mb-1">Triple Win</label>
                <input 
                  type="number"
                  className="border rounded-md px-3 py-2 w-full"
                  placeholder="500"
                  name="tripleWin"
                  value={formData.tripleWin}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="flex justify-between mt-4">
                <button 
                  type="button" 
                  className="px-4 py-2 border rounded-md bg-red-600 text-white"
                  onClick={() => handleDeleteClick(formData.id)}
                  disabled={loading}
                >
                  Delete
                </button>
                <div className="flex gap-2">
                  <button 
                    type="button" 
                    className="px-4 py-2 border rounded-md"
                    onClick={() => setShowEditModal(false)}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className={`px-4 py-2 bg-blue-600 text-white rounded-md ${loading ? 'opacity-70' : ''}`}
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update Game'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Modal - Updated for User-Specific Game */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex justify-center items-center">
          <div className="bg-white rounded-lg w-full max-w-sm p-4 max-h-[90vh] overflow-auto animate-slide-up">
            <div className="mb-4">
              <div className="py-5 bg-[#0E64D1] rounded-t-lg text-white -mx-4 -mt-4 mb-4">
                <h3 className="text-lg font-semibold text-center">Configure User-Specific Game</h3>
              </div>
              <button onClick={() => setShowAddModal(false)} className="absolute right-6 top-6 text-white">
                <X size={20} />
              </button>
            </div>
            
            {/* User info display */}
            <div className="mb-4 p-3 bg-blue-50 rounded-md border border-blue-100">
              <div className="flex items-start">
                <AlertCircle size={18} className="text-blue-500 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Configuring game for user:</p>
                  <p className="text-sm text-blue-600">{userData?.user.name} (ID: {userId})</p>
                </div>
              </div>
            </div>
            
            <form onSubmit={handleAddSubmit}>
              {/* {user && user.roleId === 1 ? (
                <div className="mb-3">
                  <label className="block text-sm mb-1">Game Settings</label>
                  <select 
                    className="border rounded-md px-3 py-2 w-full"
                    name="gameSettingsId"
                    value={formData.gameSettingsId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Game Settings</option>
                    {gameSettings.map(setting => (
                      <option key={setting.id} value={setting.id}>{setting.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">The base game configuration</p>
                </div>
              ) : ( */}
                <div className="mb-3 relative">
                  <label className="block text-sm mb-1">Select Game</label>
                  <button
                    type="button"
                    onClick={() => setUserGamesDropdownOpen(!userGamesDropdownOpen)}
                    className="w-full text-sm px-3 py-2 bg-gray-50 border border-gray-300 rounded-md focus:outline-none flex justify-between items-center"
                  >
                    <span className="font-light text-sm text-gray-500">
                      {selectedGame || "Please Select Game"}
                    </span>
                    {userGamesDropdownOpen ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>

                  {userGamesDropdownOpen && (
                    <ul className="absolute w-full mt-1 bg-white border rounded-md shadow-md z-10 max-h-60 overflow-y-auto">
                      {userGames.map((game, index) => (
                        <li
                          key={game.id}
                          onClick={() => {
                            setSelectedGame(`${game.name} (₱${toCurrency(game.winRate)}|${toCurrency(game.commissionRate)}%)`);
                            setFormData({
                              ...formData,
                              gameSettingsId: game.game_settings_id?.toString() || "",
                              gameId: game.id?.toString() || "",
                              name: game.name || "",
                              commissionRate: game.commissionRate?.toString() || "",
                              winRate: game.winRate?.toString() || "",
                              doubleWin: game.doubleWin?.toString() || "",
                              tripleWin: game.tripleWin?.toString() || ""
                            });
                            setUserGamesDropdownOpen(false);
                          }}
                          className={`p-2.5 text-xs text-black hover:bg-[#E1E7EF] cursor-pointer ${index !== 0 ? "border-t border-[#E1E7EF]" : ""}`}
                        >
                          {game.dropdownGameName}
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Select a game to use as template</p>
                </div>
              {/* )} */}
              
              <div className="mb-3">
                <label className="block text-sm mb-1">Game Name</label>
                <input 
                  type="text"
                  className="border rounded-md px-3 py-2 w-full"
                  placeholder="Enter game name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="mb-3">
                <label className="block text-sm mb-1">Commission Rate (%)</label>
                <input 
                  type="number"
                  className="border rounded-md px-3 py-2 w-full"
                  name="commissionRate"
                  value={formData.commissionRate}
                  onChange={handleInputChange}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Commission percentage for this game</p>
              </div>
              
              <div className="mb-3">
                <label className="block text-sm mb-1">Win Rate</label>
                <input 
                  type="number"
                  className="border rounded-md px-3 py-2 w-full"
                  name="winRate"
                  value={formData.winRate}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="mb-3">
                <label className="block text-sm mb-1">Double Win</label>
                <input 
                  type="number"
                  className="border rounded-md px-3 py-2 w-full"
                  name="doubleWin"
                  value={formData.doubleWin}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="mb-3">
                <label className="block text-sm mb-1">Triple Win</label>
                <input 
                  type="number"
                  className="border rounded-md px-3 py-2 w-full"
                  name="tripleWin"
                  value={formData.tripleWin}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="mb-3">
                <label className="block text-sm mb-1">Bet Limit</label>
                <input 
                  type="number"
                  className="border rounded-md px-3 py-2 w-full"
                  name="bet_limit"
                  value={formData.bet_limit}
                  onChange={handleInputChange}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Maximum bet amount allowed</p>
              </div>
              
              <div className="flex justify-end gap-2 mt-4">
                <button 
                  type="button" 
                  className="px-4 py-2 border rounded-md"
                  onClick={() => setShowAddModal(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className={`px-4 py-2 bg-blue-600 text-white rounded-md ${loading ? 'opacity-70' : ''}`}
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Create Game'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CSS for animation */}
      <style jsx="true">{`
        @keyframes slideUp {
          from {
            transform: translateY(30px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slideUp 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}

